import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { AntDHeader } from 'views/ui/Header/Header'
import { useHelmet, useHistoryPush } from '@agro-club/frontend-shared'
import { useTranslation } from 'react-i18next'
import { Button, Drawer, Flex, Input, Splitter, Typography } from 'antd'
import { SeedPedigree } from 'modules/domain/seedTracker/types'
import { getSeedInventoryGrowerCompanies } from 'modules/domain/company/managers'
import { fetchRegions } from 'modules/domain/collection/managers'
import GenericSelect from 'views/components/Generics/GenericSelect'
import type CSS from 'csstype'
import Routes from '../routes'
import { useSelector } from 'react-redux'
import SeedTrackerSelectors from 'modules/domain/seedTracker/selectors'
import { Progress } from 'modules/types'
import useDownload from 'hooks/useDownload'
import { endpoints } from 'modules/endpoints'
import ItemLoadingLayout from 'views/layouts/ItemLoadingLayout/ItemLoadingLayout'
import Item404 from 'views/layouts/Item404/Item404'
import ItemGenericError from 'views/layouts/ItemGenericError/ItemGenericError'
import SeedInventoryTable from './SeedInventoryTable'
import type { SeedInventory } from './SeedInventoryTable'
import SeedInventoryDrawerContent from './SeedInventoryDetailDrawer/SeedInventoryDrawerContent'
import SeedInventoryEdit from './SeedInventoryDetailDrawer/SeedInventoryEdit'
import {
  collapseButtonStyle,
  detailHeaderStyle,
  detailTitleRowStyle,
  detailTitleStyle,
  seedInventoryTabRootStyle,
  leftPanelFlexStyle,
  leftPanelOverlayStyle,
  rightPanelContentStyle,
  rightPanelContentNoScrollStyle,
  searchMaxWidthStyle,
  splitterContainerStyle,
  splitterLeftPanelStyle,
  splitterRightPanelStyle,
  drawerFilterStackStyle,
} from '../styles'

const { Title } = Typography

const pedigreeOptions = Object.values(SeedPedigree).map((v) => ({ label: v, value: v }))

const drawerFilterSelectProps = {
  allowClear: true as const,
  showSearch: true as const,
  parentContainerFlex: 1 as const,
}

const nameFilterOption = (input: string, option: { label?: unknown } | undefined) => {
  const label = typeof option?.label === 'string' ? option.label : String(option?.label ?? '')
  return label.toLowerCase().includes(input.toLowerCase())
}

const SeedInventoryList: React.FC = () => {
  const params = useParams<{ id: string }>()
  const location = useLocation<{ selectedCompanyId?: string } | null>()
  const push = useHistoryPush()
  const { t } = useTranslation(['seedTracker', 'labels'])
  const [selectedRow, setSelectedRow] = useState<SeedInventory | null>(null)
  const [isAdjustingInventory, setIsAdjustingInventory] = useState(false)
  const [tableRefreshKey, setTableRefreshKey] = useState(0)
  const [search, setSearch] = useState<string | undefined>(undefined)
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false)

  const [filterCompanyId, setFilterCompanyId] = useState<string | undefined>(undefined)
  const [filterSeededPedigree, setFilterSeededPedigree] = useState<string | undefined>(undefined)
  const [filterRegionId, setFilterRegionId] = useState<string | undefined>(undefined)
  const [filterYear, setFilterYear] = useState<string | undefined>(undefined)

  const [growerOptions, setGrowerOptions] = useState<{ label: string; value: string }[]>([])
  const [regionOptions, setRegionOptions] = useState<{ label: string; value: string }[]>([])

  const yearOptions = Array.from({ length: 5 }, (_, index) => {
    const year = new Date().getFullYear() - index
    return { label: String(year), value: String(year) }
  })

  useEffect(() => {
    let cancelled = false
    getSeedInventoryGrowerCompanies()
      .then((res) => {
        if (!cancelled) {
          setGrowerOptions((res.data ?? []).map((c) => ({ value: c.id, label: c.official_name })))
        }
      })
      .catch(() => {
        if (!cancelled) setGrowerOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    fetchRegions(undefined, undefined, 1, 1000)
      .then((res) => {
        if (!cancelled) {
          setRegionOptions((res.data ?? []).map((r) => ({ value: r.id, label: r.title })))
        }
      })
      .catch(() => {
        if (!cancelled) setRegionOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  useHelmet({ title: t('seedTrackerMetaTitle') })

  const seedTracker = useSelector((state) => SeedTrackerSelectors.seedTracker(state, params.id))
  const meta = useSelector((state) => SeedTrackerSelectors.meta(state, params.id))

  const goBack = () => push({ route: Routes.List })

  const loading = () => <ItemLoadingLayout id={params.id} onBack={goBack} />
  const error404 = () => <Item404 id={params.id} onBack={goBack} title={t('errors.notFoundTitle')} />
  const errorUnknown = () => <ItemGenericError id={params.id} onBack={goBack} title={t('errors.unknownErrorTitle')} />

  const handleRowClick = useCallback(
    (record: SeedInventory) => {
      if (isAdjustingInventory) {
        return
      }
      setSelectedRow(record)
    },
    [isAdjustingInventory],
  )

  const handleAdjustStart = useCallback(() => {
    setIsAdjustingInventory(true)
  }, [])

  const handleAdjustCancel = useCallback(() => {
    setIsAdjustingInventory(false)
  }, [])

  const handleAdjustSaved = useCallback((updated: SeedInventory) => {
    setSelectedRow(updated)
    setIsAdjustingInventory(false)
    setTableRefreshKey((k) => k + 1)
  }, [])

  const handleDeleted = useCallback(() => {
    setSelectedRow(null)
    setTableRefreshKey((k) => k + 1)
  }, [])

  const { Search } = Input
  const [progressCSV, downloadCSV] = useDownload(endpoints.seedInventory('download/csv'), {
    product_card_id: params.id,
    ...(filterCompanyId ? { company_id: filterCompanyId } : {}),
    ...(filterSeededPedigree ? { seeded_pedigree: filterSeededPedigree } : {}),
    ...(filterRegionId ? { region_id: filterRegionId } : {}),
    ...(filterYear ? { year: filterYear } : {}),
  })

  const growerFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.seedInventoryFilters.growerLabel'),
    placeholder: t('list.seedInventoryFilters.growerPlaceholder'),
    options: growerOptions,
    value: filterCompanyId,
    onChange: (value) => setFilterCompanyId((value as string | undefined) ?? undefined),
    filterOption: nameFilterOption,
    ...drawerFilterSelectProps,
  }

  const pedigreeFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.seedInventoryFilters.pedigreeLabel'),
    placeholder: t('list.seedInventoryFilters.pedigreePlaceholder'),
    options: pedigreeOptions,
    value: filterSeededPedigree,
    onChange: (value) => setFilterSeededPedigree((value as string | undefined) ?? undefined),
    ...drawerFilterSelectProps,
  }

  const regionFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.seedInventoryFilters.regionLabel'),
    placeholder: t('list.seedInventoryFilters.regionPlaceholder'),
    options: regionOptions,
    value: filterRegionId,
    onChange: (value) => setFilterRegionId((value as string | undefined) ?? undefined),
    filterOption: nameFilterOption,
    ...drawerFilterSelectProps,
  }

  const yearFilterProps: React.ComponentProps<typeof GenericSelect> = {
    title: t('list.seedInventoryFilters.yearLabel', 'Year'),
    placeholder: t('list.seedInventoryFilters.yearPlaceholder', 'Select year'),
    options: yearOptions,
    value: filterYear,
    onChange: (value) => setFilterYear((value as string | undefined) ?? undefined),
    ...drawerFilterSelectProps,
  }

  const filterBarLeftFull = (
    <Flex vertical gap="middle" style={{ width: '100%' }}>
      <Flex justify="space-between" align="center" gap="middle" wrap="wrap">
        <Search
          placeholder={t('list.searchPlaceholder')}
          onSearch={(value) => setSearch(value || undefined)}
          value={search ?? ''}
          allowClear
          onChange={(e) => setSearch(e.target.value || undefined)}
          style={searchMaxWidthStyle}
        />
        <Flex gap="middle" wrap="wrap">
          <Button type="default" onClick={downloadCSV} loading={progressCSV === Progress.WORK}>
            {t('labels:downloadCsv')}
          </Button>
          <Button type="primary" onClick={() => push({ route: Routes.SeedInventoryAdd, params: { id: params.id! } })}>
            {t('detail.seedInventory.trackNewInventory')}
          </Button>
        </Flex>
      </Flex>
      <Flex gap="middle" align="center" wrap="wrap">
        <GenericSelect {...growerFilterProps} />
        <GenericSelect {...pedigreeFilterProps} />
        <GenericSelect {...regionFilterProps} />
        <GenericSelect {...yearFilterProps} />
      </Flex>
    </Flex>
  )

  const filterBarLeftCompact = (
    <Button type="default" onClick={() => setFiltersDrawerOpen(true)} disabled={isAdjustingInventory}>
      {t('detail.seedInventory.filtersButton')}
    </Button>
  )

  if (meta.fetchProgress === Progress.ERROR) {
    if (meta.fetchError === 'not_found') {
      return error404()
    }
    return errorUnknown()
  }

  if (meta.fetchProgress === Progress.WORK || !seedTracker) {
    return loading()
  }

  // Build title: "[Variety] - [Class] [Crop]"
  const title = `${seedTracker.variety} - ${seedTracker.seed_class_name} ${seedTracker.seed_crop_name}`

  return (
    <>
      <AntDHeader onClickBack={goBack} styles={detailHeaderStyle as CSS.Properties} />
      <Flex align="center" style={detailTitleRowStyle}>
        <Title level={2} style={detailTitleStyle}>
          {title}
        </Title>
      </Flex>
      <div style={seedInventoryTabRootStyle}>
        {!selectedRow ? (
          <SeedInventoryTable
            filterBarLeft={filterBarLeftFull}
            productCardId={params.id!}
            initialSelectedCompanyId={location.state?.selectedCompanyId}
            onRowClick={handleRowClick}
            compactColumns={false}
            selectedRecordId={null}
            refreshKey={tableRefreshKey}
            companyId={filterCompanyId}
            seededPedigree={filterSeededPedigree}
            regionId={filterRegionId}
            year={filterYear}
          />
        ) : (
          <div style={splitterContainerStyle}>
            <Splitter style={splitterContainerStyle}>
              <Splitter.Panel size="20%" resizable={false} style={splitterLeftPanelStyle}>
                <Flex vertical style={leftPanelFlexStyle}>
                  <SeedInventoryTable
                    filterBarLeft={filterBarLeftCompact}
                    productCardId={params.id!}
                    initialSelectedCompanyId={location.state?.selectedCompanyId}
                    onRowClick={handleRowClick}
                    compactColumns={true}
                    selectedRecordId={selectedRow.id}
                    lockRowSelection={isAdjustingInventory}
                    refreshKey={tableRefreshKey}
                    companyId={filterCompanyId}
                    seededPedigree={filterSeededPedigree}
                    regionId={filterRegionId}
                    year={filterYear}
                  />
                </Flex>
                {isAdjustingInventory ? <div aria-hidden style={leftPanelOverlayStyle} /> : null}
              </Splitter.Panel>
              <Splitter.Panel size="80%" style={splitterRightPanelStyle}>
                <Button
                  type="default"
                  aria-label={t('detail.seedInventory.collapseDetailPanel')}
                  title={t('detail.seedInventory.collapseDetailPanel')}
                  onClick={() => setSelectedRow(null)}
                  disabled={isAdjustingInventory}
                  style={collapseButtonStyle}
                >
                  &gt;
                </Button>
                <Flex vertical style={isAdjustingInventory ? rightPanelContentStyle : rightPanelContentNoScrollStyle}>
                  {isAdjustingInventory ? (
                    <SeedInventoryEdit record={selectedRow} onCancel={handleAdjustCancel} onSaved={handleAdjustSaved} />
                  ) : (
                    <SeedInventoryDrawerContent
                      record={selectedRow}
                      onAdjustInventory={handleAdjustStart}
                      onDeleted={handleDeleted}
                      isAdjusting={isAdjustingInventory}
                    />
                  )}
                </Flex>
              </Splitter.Panel>
            </Splitter>
          </div>
        )}
        <Drawer
          title={t('detail.seedInventory.filtersDrawerTitle')}
          placement="right"
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          width={378}
          destroyOnClose={false}
        >
          <Flex vertical gap="middle">
            <div style={drawerFilterStackStyle}>
              <GenericSelect {...growerFilterProps} />
            </div>
            <div style={drawerFilterStackStyle}>
              <GenericSelect {...pedigreeFilterProps} />
            </div>
            <div style={drawerFilterStackStyle}>
              <GenericSelect {...regionFilterProps} />
            </div>
            <div style={drawerFilterStackStyle}>
              <GenericSelect {...yearFilterProps} />
            </div>
          </Flex>
        </Drawer>
      </div>
    </>
  )
}

export default SeedInventoryList
